import { supabase } from '../../../services/supabaseClient';

const TABLE = 'bianca_realestate_email_sends';
const MF_TOKEN = 'QOQMRWtDQA9W8pluLNSV';

/**
 * Lista todos os sends (com template_id) — usado pra computar stats agregadas
 * por template na view de listagem. Agrupa por template_id no client.
 */
export async function listAllSendsWithTemplate({ limit = 20000 } = {}) {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('token', MF_TOKEN)
    .not('template_id', 'is', null)
    .order('sent_at', { ascending: false })
    .limit(limit);
  if (error) return { ok: false, error: error.message, items: [] };
  return { ok: true, items: data || [] };
}

/**
 * Recebe a lista de sends e devolve um map { [template_id]: stats }.
 */
export function statsByTemplate(allSends) {
  const map = new Map();
  for (const s of allSends) {
    const key = s.template_id;
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  const out = {};
  for (const [tid, sends] of map.entries()) {
    out[tid] = aggregateStats(sends);
  }
  return out;
}

/**
 * Lista todos os sends de um template. Ordena por sent_at desc.
 */
export async function listSendsByTemplate(templateId, { limit = 5000 } = {}) {
  if (!templateId) return { ok: true, items: [] };
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('token', MF_TOKEN)
    .eq('template_id', templateId)
    .order('sent_at', { ascending: false })
    .limit(limit);
  if (error) return { ok: false, error: error.message, items: [] };
  return { ok: true, items: data || [] };
}

/**
 * Lista sends de um run específico (último disparo, geralmente).
 */
export async function listSendsByRun(runId, { limit = 5000 } = {}) {
  if (!runId) return { ok: true, items: [] };
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('token', MF_TOKEN)
    .eq('run_id', runId)
    .order('sent_at', { ascending: false })
    .limit(limit);
  if (error) return { ok: false, error: error.message, items: [] };
  return { ok: true, items: data || [] };
}

/**
 * Agrega stats a partir de uma lista de sends.
 */
export function aggregateStats(sends) {
  const total = sends.length;
  let delivered = 0, opened = 0, clicked = 0, bounced = 0, complained = 0, failed = 0;
  let totalOpens = 0, totalClicks = 0;
  const bounces = [];
  const runsSet = new Set();

  for (const s of sends) {
    if (s.run_id) runsSet.add(s.run_id);
    if (s.delivered_at) delivered++;
    if (s.opened_at) opened++;
    if (s.clicked_at) clicked++;
    if (s.status === 'bounced') {
      bounced++;
      bounces.push({ email: s.recipient_email, reason: s.bounce_reason, when: s.bounced_at });
    }
    if (s.status === 'complained') complained++;
    if (s.status === 'failed') failed++;
    totalOpens += s.opened_count || 0;
    totalClicks += s.clicked_count || 0;
  }

  return {
    total,
    delivered, opened, clicked,
    bounced, complained, failed,
    totalOpens, totalClicks,
    runs: runsSet.size,
    bounces,
    rates: {
      delivered: total ? Math.round((delivered / total) * 100) : 0,
      opened: total ? Math.round((opened / total) * 100) : 0,
      clicked: total ? Math.round((clicked / total) * 100) : 0,
      bounced: total ? Math.round((bounced / total) * 100) : 0,
    },
  };
}

/**
 * Lista runs de um template (agrupa por run_id, mais recente primeiro).
 */
export function groupByRun(sends) {
  const map = new Map();
  for (const s of sends) {
    const key = s.run_id || '__no_run__';
    if (!map.has(key)) {
      map.set(key, { run_id: s.run_id || null, sent_at: s.sent_at, sends: [] });
    }
    const entry = map.get(key);
    entry.sends.push(s);
    if (s.sent_at && s.sent_at < entry.sent_at) entry.sent_at = s.sent_at;
  }
  const list = Array.from(map.values()).map(g => ({
    ...g,
    stats: aggregateStats(g.sends),
  }));
  list.sort((a, b) => (a.sent_at < b.sent_at ? 1 : -1));
  return list;
}
